import { PageBlueprint, createFrontendModule, createRouteRef } from '@backstage/frontend-plugin-api';
import MapIcon from '@material-ui/icons/MyLocation';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import DashboardRounded from '@material-ui/icons/DashboardRounded';
import SecurityRounded from '@material-ui/icons/SecurityRounded';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import HomeIcon from '@material-ui/icons/Home';

/**
 * Each bespoke overview page is registered as a native PageBlueprint extension.
 * This removes them from the legacy FlatRoutes block and makes them first-class
 * New Frontend System features.  They appear automatically in the sidebar via
 * `nav.rest()` in `modules/nav/Sidebar.tsx` (sorted alphabetically).
 *
 * Each page MUST have an explicit `routeRef` so that `NavContentRenderer`
 * (AppNav.esm.js) can resolve the nav item href and include it in `navItems`.
 * Without a routeRef, `node.instance.getData(coreExtensionData.routeRef)`
 * returns undefined and the item is silently filtered out.
 */

// Route refs — one per bespoke page (required for nav item resolution)
const homeRouteRef = createRouteRef();
const adrRouteRef = createRouteRef();
const faastRouteRef = createRouteRef();
const techRadarRouteRef = createRouteRef();
const architectureDiagramsRouteRef = createRouteRef();
const apiGuideRouteRef = createRouteRef();

const homePage = PageBlueprint.make({
    name: 'home',
    params: {
        noHeader: true,
        path: '/',
        routeRef: homeRouteRef,
        title: 'Home',
        icon: <HomeIcon />,
        loader: async () => {
            const { default: HomePage } = await import('../components/HomePage');
            return <HomePage />;
        },
    },
});

const adrPage = PageBlueprint.make({
    name: 'adr',
    params: {
        noHeader: true,
        // Path matches the deepLink used in HomePage (/adr-overview)
        path: '/adr-overview',
        routeRef: adrRouteRef,
        title: 'ADR',
        icon: <DashboardRounded />,
        loader: async () => {
            const { ADROverview } = await import('../components/adr-overview/ADROverview');
            return <ADROverview />;
        },
    },
});

const faastPage = PageBlueprint.make({
    name: 'faast',
    params: {
        noHeader: true,
        path: '/faast-overview',
        routeRef: faastRouteRef,
        title: 'FAAST',
        icon: <SecurityRounded />,
        loader: async () => {
            const { FaastOverview } = await import('../components/faast-overview/FaastOverview');
            return <FaastOverview />;
        },
    },
});

const techRadarPage = PageBlueprint.make({
    name: 'tech-radar-overview',
    params: {
        noHeader: true,
        path: '/tech-radar-overview',
        routeRef: techRadarRouteRef,
        title: 'Tech Radar',
        icon: <MapIcon />,
        loader: async () => {
            const { TechRadarOverview } = await import('../components/tech-radar-overview/TechRadarOverview');
            return <TechRadarOverview />;
        },
    },
});

const architectureDiagramsPage = PageBlueprint.make({
    name: 'architecture-diagrams',
    params: {
        noHeader: true,
        path: '/architecture-diagrams',
        routeRef: architectureDiagramsRouteRef,
        title: 'Architecture Diagrams',
        icon: <AccountTreeIcon />,
        loader: async () => {
            const { ArchitectureDiagramsOverview } = await import('../components/architecture-diagrams/ArchitectureDiagramsOverview');
            return <ArchitectureDiagramsOverview />;
        },
    },
});

const apiGuidePage = PageBlueprint.make({
    name: 'api-guide',
    params: {
        noHeader: true,
        path: '/api-guide',
        routeRef: apiGuideRouteRef,
        title: 'API Guide',
        icon: <MenuBookIcon />,
        loader: async () => {
            const { ApiGuide } = await import('../components/api-guide');
            return <ApiGuide />;
        },
    },
});

/**
 * Frontend module that registers all bespoke overview pages as native New
 * Frontend System page extensions, replacing the legacy route entries that
 * were previously in `App.tsx`'s `FlatRoutes` block.
 */
export const customPagesModule = createFrontendModule({
    pluginId: 'app',
    extensions: [homePage, adrPage, faastPage, techRadarPage, architectureDiagramsPage, apiGuidePage],
});

